/**
 * tutorialContent.js — the FarmTopia platform usage guide, ported from the
 * platform V2 Next app (public/locales/{en,ar}/tutorial.json +
 * components/Tutorial/TutorialLayout.tsx) into the landing CRA.
 *
 * Mirrors the /crops data pattern: a single source array consumed by the
 * /guide index (Guide.jsx) and /guide/:slug detail pages (GuideStory.jsx).
 *
 * TUTORIAL_SECTIONS preserves the canonical walkthrough order — it drives the
 * card grid and the prev/next pager. Each section carries EN + AR copy and a
 * `body` array of typed blocks:
 *   { type: "paragraph", text }              — a prose paragraph
 *   { type: "step", title, text }            — a numbered walkthrough step
 *   { type: "tip", title, text }             — a field tip (add-farm only)
 *   { type: "heading", text }                — a section sub-heading
 *   { type: "cta", title, text, button, to } — the closing call-to-action card
 */

/** Canonical section order — drives the card grid and prev/next pager. */
export const TUTORIAL_SECTIONS = [
  "onboarding",
  "subscription",
  "weather",
  "team",
  "settings",
  "finance",
  "add-farm",
  "add-field",
  "fertilizer",
  "irrigation",
  "report",
  "task",
  "hathor-ai",
  "crop-data",
  "connect-sensor",
];

/** Index-page strings (eyebrow / title / description / card CTA), EN + AR. */
export const TUTORIAL_INDEX = {
  en: {
    eyebrow: "Product walkthrough",
    title: "Get started with FarmTopia in fifteen steps",
    description:
      "A guided tour of the FarmTopia platform — sign in, pick a plan, draw your farms and fields, then dive into weather, irrigation, finance, and the Hathor AI agronomist. Built for Egyptian growers and ready in under an hour.",
    stepLabel: "Step",
    cardCta: "Read the walkthrough",
    breadcrumbHome: "Guide",
    pagerPrev: "Previous",
    pagerNext: "Next",
  },
  ar: {
    eyebrow: "جولة في المنصة",
    title: "ابدأ مع FarmTopia في خمس عشرة خطوة",
    description:
      "جولة موجَّهة في منصة FarmTopia — سجل دخولك، اختر باقة مناسبة، ارسم مزارعك وحقولك، ثم انطلق إلى الطقس والري والتمويل ومساعد الذكاء الاصطناعي حتحور. مبنية لأهل الأرض في مصر وجاهزة في أقل من ساعة.",
    stepLabel: "الخطوة",
    cardCta: "اقرأ الشرح",
    breadcrumbHome: "الدليل",
    pagerPrev: "السابق",
    pagerNext: "التالي",
  },
};

/**
 * The 15 guide sections, in TUTORIAL_SECTIONS order. Titles, eyebrows, blurbs
 * and body copy are ported verbatim from tutorial.json (EN + AR).
 */
export const TUTORIAL_CONTENT = [
  {
    slug: "onboarding",
    image: "/tutorial/onboarding.webp",
    en: {
      title: "Sign in to your FarmTopia account",
      eyebrow: "Step 1 of 15",
      blurb:
        "Two ways to get into the platform — email and password, or your Google account. Either takes under a minute.",
      imageAlt:
        "FarmTopia login screen wireframe showing the email and password form on the left and a hero panel with the Egyptian flag on the right.",
      body: [
        {
          type: "paragraph",
          text: "Two routes into the platform: email + password, or one-click with Google. Both land you at your dashboard in under a minute.",
        },
        {
          type: "paragraph",
          text: "FarmTopia's sign-in page works the way you'd expect — but the design is built around Egyptian growers, with full Arabic RTL support and a layout that holds up on phones, tablets, and the agronomist's laptop.",
        },
        {
          type: "step",
          title: "Open the sign-in page",
          text: "Navigate to farmtopia.farm/login from any browser. The page loads in under a second on a 3G connection — no app install required.",
        },
        {
          type: "step",
          title: "Enter your email and password",
          text: "Use the credentials you created at sign-up. If you typed your email correctly we'll remember it next time you visit the forgot-password page.",
        },
        {
          type: "step",
          title: "Or sign in with Google",
          text: "One click. We never ask for permission to send email or post on your behalf — only to identify you.",
        },
        {
          type: "step",
          title: "Land on your dashboard",
          text: "First-time users see the 'Add your first farm' prompt. Returning users land on their most recently viewed farm.",
        },
        {
          type: "cta",
          title: "Ready to sign in?",
          text: "Already have an account? Jump straight to the login screen.",
          button: "Sign in now",
        },
      ],
    },
    ar: {
      title: "تسجيل الدخول إلى حسابك في FarmTopia",
      eyebrow: "الخطوة 1 من 15",
      blurb:
        "طريقتان للدخول إلى المنصة — البريد الإلكتروني وكلمة المرور، أو حساب جوجل. كلاهما يستغرق أقل من دقيقة.",
      imageAlt:
        "مخطط صفحة تسجيل الدخول في FarmTopia يعرض نموذج البريد وكلمة المرور على اليسار ولوحة بارزة مع العلم المصري على اليمين.",
      body: [
        {
          type: "paragraph",
          text: "طريقان للوصول إلى المنصة: البريد الإلكتروني + كلمة المرور، أو نقرة واحدة عبر جوجل. كلاهما يوصلك إلى لوحة التحكم في أقل من دقيقة.",
        },
        {
          type: "paragraph",
          text: "صفحة الدخول في FarmTopia تعمل كما تتوقع — لكن التصميم مبني لخدمة المزارع المصري، بدعم كامل للعربية من اليمين لليسار وتخطيط يعمل بكفاءة على الهواتف والأجهزة اللوحية والكمبيوتر.",
        },
        {
          type: "step",
          title: "افتح صفحة تسجيل الدخول",
          text: "انتقل إلى farmtopia.farm/login من أي متصفح. الصفحة تفتح في أقل من ثانية على شبكة 3G — بدون الحاجة لتثبيت أي تطبيق.",
        },
        {
          type: "step",
          title: "أدخل بريدك الإلكتروني وكلمة المرور",
          text: "استخدم البيانات التي أنشأتها عند التسجيل. لو كتبت بريدك بشكل صحيح، سنتذكره عند زيارتك صفحة استرجاع كلمة المرور.",
        },
        {
          type: "step",
          title: "أو سجّل الدخول عبر جوجل",
          text: "نقرة واحدة. لا نطلب أبداً صلاحية إرسال البريد أو النشر باسمك — فقط للتعرف عليك.",
        },
        {
          type: "step",
          title: "ادخل إلى لوحة التحكم",
          text: "المستخدمون الجدد يرون رسالة 'أضف أول مزرعة'. المستخدمون العائدون يدخلون مباشرة على آخر مزرعة شاهدوها.",
        },
        {
          type: "cta",
          title: "جاهز للدخول؟",
          text: "لديك حساب بالفعل؟ ادخل مباشرة إلى صفحة تسجيل الدخول.",
          button: "تسجيل الدخول الآن",
        },
      ],
    },
  },
  {
    slug: "subscription",
    image: "/tutorial/subscription.webp",
    en: {
      title: "Choose a subscription that fits your farm",
      eyebrow: "Step 2 of 15",
      blurb:
        "Three plans sized for hobby plots, family farms, and large-scale operations. Free tier is genuinely free — no credit card needed.",
      imageAlt:
        "FarmTopia pricing page wireframe showing three plan cards and a video walkthrough indicator on the middle plan. (The wireframe shows an earlier tier naming; the current tiers are Free Plan, Small Farm, and Medium Farm.)",
      body: [
        {
          type: "paragraph",
          text: "FarmTopia has three self-serve tiers — Free Plan (EGP 0), Small Farm (EGP 200/mo), and Medium Farm (EGP 950/mo) — plus an Enterprise plan on request. All tiers include realtime weather and Sentinel satellite imagery; Smart Irrigation Scheduling starts at Small Farm, and AI reports come with Medium Farm. Prices are indicative — the live plans page shows current pricing.",
        },
        {
          type: "paragraph",
          text: "Most farmers start on the Free Plan to validate the platform on a single plot, then move up to Small Farm or Medium Farm as they onboard their full operation. You can switch plans at any time without losing data.",
        },
        {
          type: "heading",
          text: "How to subscribe",
        },
        {
          type: "step",
          title: "Compare the three plans",
          text: "Read the feature matrix in the wireframe. The most common starting point for new growers is Small Farm (EGP 200/mo) — it covers up to two farms with full satellite + weather coverage and Smart Irrigation Scheduling included.",
        },
        {
          type: "step",
          title: "Pay with PayPal or via Google Play",
          text: "Both options support monthly and annual billing. Annual billing gets a 2-month discount applied at checkout.",
        },
        {
          type: "step",
          title: "Plan activates immediately",
          text: "Your new limits (farms, fields, users) become available the moment your payment processor confirms. No waiting.",
        },
        {
          type: "cta",
          title: "Browse the plans",
          text: "See the live pricing page with current promotions.",
          button: "View plans",
        },
      ],
    },
    ar: {
      title: "اختر الاشتراك المناسب لمزرعتك",
      eyebrow: "الخطوة 2 من 15",
      blurb:
        "ثلاث خطط مصمَّمة للأراضي الصغيرة والمزارع العائلية والعمليات الكبيرة. الباقة المجانية مجانية فعلاً — لا تحتاج بطاقة ائتمان.",
      imageAlt:
        "مخطط صفحة الأسعار في FarmTopia يعرض ثلاث بطاقات للباقات مع زر تشغيل فيديو شرح على البطاقة الوسطى. (المخطط يعرض تسمية باقات أقدم؛ الباقات الحالية هي: المجانية، المزرعة الصغيرة، والمزرعة المتوسطة.)",
      body: [
        {
          type: "paragraph",
          text: "FarmTopia بها ثلاث باقات ذاتية — الباقة المجانية (0 جنيه)، مزرعة صغيرة (200 جنيه/شهر)، ومزرعة متوسطة (950 جنيه/شهر) — بالإضافة إلى باقة Enterprise عند الطلب. كل الباقات تشمل بيانات الطقس الفورية وصور أقمار Sentinel؛ جدولة الري الذكي تبدأ من باقة المزرعة الصغيرة، وتقارير الذكاء الاصطناعي مع باقة المزرعة المتوسطة. الأسعار استرشادية — صفحة الباقات الحية تعرض السعر الحالي.",
        },
        {
          type: "paragraph",
          text: "معظم المزارعين يبدؤون بالباقة المجانية لتجربة المنصة على قطعة واحدة، ثم يرتقون إلى باقة المزرعة الصغيرة أو المتوسطة عند إدخال كامل عملياتهم. يمكنك التنقل بين الباقات في أي وقت دون فقدان البيانات.",
        },
        {
          type: "heading",
          text: "كيف تشترك",
        },
        {
          type: "step",
          title: "قارن الباقات الثلاث",
          text: "اقرأ مقارنة المزايا في المخطط. نقطة البداية الأكثر شيوعاً للمزارعين الجدد هي باقة المزرعة الصغيرة (200 جنيه/شهر) — تغطي حتى مزرعتين بتغطية كاملة للأقمار والطقس ومع جدولة الري الذكي.",
        },
        {
          type: "step",
          title: "ادفع عبر PayPal أو متجر Google Play",
          text: "كلا الخيارين يدعم الدفع الشهري والسنوي. الدفع السنوي يحصل على خصم شهرين عند الدفع.",
        },
        {
          type: "step",
          title: "تفعيل فوري للباقة",
          text: "حدودك الجديدة (المزارع والحقول والمستخدمين) تصبح متاحة بمجرد تأكيد عملية الدفع. بدون انتظار.",
        },
        {
          type: "cta",
          title: "تصفح الباقات",
          text: "اطلع على صفحة الأسعار الحية مع العروض الحالية.",
          button: "عرض الباقات",
        },
      ],
    },
  },
  {
    slug: "weather",
    image: "/tutorial/weather.webp",
    en: {
      title: "Read the weather forecast for your farm",
      eyebrow: "Step 3 of 15",
      blurb:
        "Temperature, air quality, ET₀, irradiance, and a 30-day evapotranspiration trend, all pinned to your farm's coordinates.",
      imageAlt:
        "FarmTopia weather forecast wireframe showing temperature, air quality, sunrise/sunset, irradiance, humidity, wind, precipitation, pressure, air density, cloud cover, and an evapotranspiration rate chart, with a satellite map on the right.",
      body: [
        {
          type: "paragraph",
          text: "FarmTopia's weather page pulls temperature, air quality, irradiance, humidity, wind, precipitation, and evapotranspiration for the exact polygon you drew on the map. No need to read a city forecast and translate.",
        },
        {
          type: "paragraph",
          text: "The forecast pins to your farm's centroid, not the nearest weather station. For a plot near El Mara3i in Western Giza, that can mean a 1.5 °C difference from the official Giza reading — enough to change an irrigation call.",
        },
        {
          type: "step",
          title: "Open the weather page",
          text: "From any farm screen, click Weather in the side nav — the page is titled 'Weather Dashboard'. The header shows the farm name and today's date so you always know what you're reading.",
        },
        {
          type: "step",
          title: "Read the headline card",
          text: "Temperature, sky condition, air quality (1–5 scale), CO₂ and N₂ levels, plus sunrise, sunset, moonrise, and moonset — the data set most growers reach for first.",
        },
        {
          type: "step",
          title: "Scan the eight metric tiles",
          text: "Irradiance, humidity, wind, precipitation, pressure, air density, cloud percentage, and evapotranspiration — each shown in mW/m² or the matching unit. Hover for the data source.",
        },
        {
          type: "step",
          title: "Watch the ET₀ trend",
          text: "The evapotranspiration chart toggles between hourly and daily, with the current month overlaid against the past month. A rising ET₀ line is your early signal to bring forward the next irrigation.",
        },
        {
          type: "step",
          title: "Cross-check with satellite imagery",
          text: "The map on the right defaults to Sentinel-2. Use the date picker to compare today's NDVI against last week — a sudden drop combined with high ET₀ usually means water stress.",
        },
        {
          type: "cta",
          title: "Open the live weather page",
          text: "See today's forecast for the farm currently selected in your dashboard.",
          button: "Open weather",
        },
      ],
    },
    ar: {
      title: "اقرأ توقعات الطقس لمزرعتك",
      eyebrow: "الخطوة 3 من 15",
      blurb:
        "درجة الحرارة، جودة الهواء، البخر-نتح، الإشعاع الشمسي، واتجاه البخر-نتح لـ 30 يوماً — مثبّتة على إحداثيات مزرعتك بالضبط.",
      imageAlt:
        "مخطط صفحة الطقس في FarmTopia يعرض درجة الحرارة وجودة الهواء وشروق وغروب الشمس والقمر، والإشعاع الشمسي والرطوبة والرياح والأمطار والضغط وكثافة الهواء ونسبة الغيوم ومعدل البخر-نتح في مخطط، مع خريطة الأقمار على اليمين.",
      body: [
        {
          type: "paragraph",
          text: "صفحة الطقس في FarmTopia تعرض درجة الحرارة وجودة الهواء والإشعاع الشمسي والرطوبة والرياح والأمطار والبخر-نتح للقطعة التي رسمتها على الخريطة بالضبط. لا حاجة لقراءة توقعات المحافظة وتقديرها بنفسك.",
        },
        {
          type: "paragraph",
          text: "التوقع مثبّت على مركز مزرعتك، لا على أقرب محطة. لقطعة بجوار المراعي في غرب الجيزة، قد يعني ذلك فرق 1.5 درجة مئوية عن قراءة الجيزة الرسمية — فرق يكفي لتغيير قرار الري.",
        },
        {
          type: "step",
          title: "افتح صفحة الطقس",
          text: "من أي شاشة مزرعة، اضغط على Weather في قائمة الجانب — الصفحة بعنوان 'لوحة الطقس'. الترويسة تعرض اسم المزرعة وتاريخ اليوم لتعرف بالضبط ما الذي تقرأه.",
        },
        {
          type: "step",
          title: "اقرأ البطاقة الرئيسية",
          text: "درجة الحرارة، حالة السماء، جودة الهواء (مقياس 1-5)، مستويات CO₂ وN₂، وأوقات شروق وغروب الشمس والقمر — مجموعة البيانات التي يبحث عنها معظم المزارعين أولاً.",
        },
        {
          type: "step",
          title: "تفحّص بطاقات المقاييس الثمانية",
          text: "الإشعاع، الرطوبة، الرياح، الأمطار، الضغط، كثافة الهواء، نسبة الغيوم، والبخر-نتح — كل قيمة بـ mW/m² أو الوحدة المناسبة. مرّر للحصول على مصدر البيانات.",
        },
        {
          type: "step",
          title: "تابع اتجاه ET₀",
          text: "مخطط البخر-نتح ينتقل بين الساعة واليوم، ويعرض الشهر الحالي فوق الشهر الماضي. ارتفاع خط ET₀ هو إشارة مبكرة لتقديم موعد الرية القادمة.",
        },
        {
          type: "step",
          title: "قارن مع صور الأقمار",
          text: "الخريطة على اليمين تستخدم Sentinel-2 افتراضياً. استخدم منتقي التاريخ لمقارنة NDVI اليوم مع الأسبوع الماضي — انخفاض مفاجئ مع ارتفاع ET₀ يعني عادةً إجهاد مائي.",
        },
        {
          type: "cta",
          title: "افتح صفحة الطقس الحية",
          text: "اطّلع على توقع اليوم للمزرعة المحددة حالياً في لوحة التحكم.",
          button: "افتح الطقس",
        },
      ],
    },
  },
  {
    slug: "team",
    image: "/tutorial/team.webp",
    en: {
      title: "Invite your team and assign roles",
      eyebrow: "Step 4 of 15",
      blurb:
        "Add agronomists, assistant managers, and field workers, then control what each one sees and edits across your farms.",
      imageAlt:
        "FarmTopia team management wireframe showing a Team table with 12 members, columns for name, email, and role (Farm Owner, Assistant Farm Manager, Worker), with Invite Teammates and Shareable Link buttons in the header.",
      body: [
        {
          type: "paragraph",
          text: "Add agronomists, assistant farm managers, and field workers to a farm with one of three roles. Each role has its own permission scope so the wrong person can't accidentally edit the irrigation plan.",
        },
        {
          type: "paragraph",
          text: "On most Egyptian operations the owner does the strategy, the assistant manager does the day-to-day, and the workers execute tasks in the field. FarmTopia mirrors that hierarchy out of the box — you don't have to design permissions from scratch.",
        },
        {
          type: "step",
          title: "Open the Team page",
          text: "Click Team in the side nav (the page is titled 'Team Management'). A stats strip shows Total Members, Owners, Managers, and Workers, above a searchable, sortable list of everyone with access to this workspace.",
        },
        {
          type: "step",
          title: "Invite by email or shareable link",
          text: "Use Invite Teammates to email a single person, or Shareable Link to generate a join link you can drop in WhatsApp. Either way the recipient sets their own password on first sign-in.",
        },
        {
          type: "step",
          title: "Pick the right role",
          text: "Farm Owner edits everything. Assistant Farm Manager edits day-to-day data but can't change subscription or delete farms. Worker only sees tasks assigned to them.",
        },
        {
          type: "step",
          title: "Switch views and export",
          text: "Toggle between card and table views, and use Export to download a CSV of the team for HR or payroll. The list paginates at 20 per page — useful for operations with hundreds of seasonal workers. (Advanced role filtering is on the way.)",
        },
        {
          type: "cta",
          title: "Open team management",
          text: "Add a teammate, change a role, or remove access in under a minute.",
          button: "Open team",
        },
      ],
    },
    ar: {
      title: "ادعُ فريقك ووزِّع الأدوار",
      eyebrow: "الخطوة 4 من 15",
      blurb:
        "أضف المهندسين الزراعيين ومساعدي المدير والعمال، وتحكّم في ما يراه كل واحد ويعدّله في مزارعك.",
      imageAlt:
        "مخطط إدارة الفريق في FarmTopia يعرض جدول الفريق بـ 12 عضواً، أعمدة للاسم والبريد الإلكتروني والدور (مالك المزرعة، مساعد مدير المزرعة، عامل)، مع أزرار 'دعوة أعضاء' و'رابط للمشاركة' في الترويسة.",
      body: [
        {
          type: "paragraph",
          text: "أضف مهندسين زراعيين ومساعدي مدير وعمالاً إلى مزرعتك بأحد ثلاثة أدوار. لكل دور نطاق صلاحيات خاص حتى لا يعدّل الشخص غير المناسب خطة الري بالخطأ.",
        },
        {
          type: "paragraph",
          text: "في معظم العمليات المصرية، المالك يضع الاستراتيجية، والمساعد يدير اليوم، والعمال ينفذون المهام في الحقل. FarmTopia تعكس هذا التسلسل افتراضياً — لا تحتاج لتصميم الصلاحيات من الصفر.",
        },
        {
          type: "step",
          title: "افتح صفحة الفريق",
          text: "اضغط على Team في قائمة الجانب (الصفحة بعنوان 'إدارة الفريق'). شريط إحصائيات يعرض إجمالي الأعضاء والمُلّاك والمديرين والعمال، فوق قائمة قابلة للبحث والفرز لكل من له صلاحية الدخول.",
        },
        {
          type: "step",
          title: "ادعُ بالبريد أو برابط للمشاركة",
          text: "استخدم 'دعوة أعضاء' لإرسال بريد لشخص واحد، أو 'رابط للمشاركة' لإنشاء رابط انضمام تنشره على واتساب. في الحالتين، المستقبل يضع كلمة مروره عند أول دخول.",
        },
        {
          type: "step",
          title: "اختر الدور المناسب",
          text: "مالك المزرعة يعدّل كل شيء. مساعد المدير يعدّل البيانات اليومية لكن لا يستطيع تغيير الاشتراك أو حذف المزارع. العامل يرى فقط المهام الموكّلة إليه.",
        },
        {
          type: "step",
          title: "بدّل العرض وصدّر",
          text: "بدّل بين عرض البطاقات والجدول، واستخدم 'تصدير' لتنزيل قائمة الفريق بصيغة CSV للموارد البشرية أو الرواتب. القائمة تنقسم 20 لكل صفحة — مفيد لعمليات بمئات العمال الموسميين. (التصفية المتقدمة بالأدوار قادمة قريباً.)",
        },
        {
          type: "cta",
          title: "افتح إدارة الفريق",
          text: "أضف عضواً، غيّر دوراً، أو احذف صلاحية في أقل من دقيقة.",
          button: "افتح الفريق",
        },
      ],
    },
  },
  {
    slug: "settings",
    image: "/tutorial/settings.webp",
    en: {
      title: "Configure your account and preferences",
      eyebrow: "Step 5 of 15",
      blurb:
        "Personal info, login security, notifications, language, payments, and farm-level settings — all in one place.",
      imageAlt:
        "FarmTopia settings wireframe showing the Account header with the user's name and email, followed by eight tile cards: Personal info, Login & security, Payments & payouts, Farm Settings, Notifications, Privacy & sharing, Global preferences, and Support and Feedback.",
      body: [
        {
          type: "paragraph",
          text: "One page, eight tiles — personal info, login security, payments, farm settings, notifications, privacy, language, and support. Everything you need to make FarmTopia feel like your own tool.",
        },
        {
          type: "paragraph",
          text: "Most growers visit Settings twice: once on day one to set language to Arabic and currency to EGP, and again when they add a new payment method or onboard a co-owner. The page is built for those two journeys.",
        },
        {
          type: "step",
          title: "Update your personal info",
          text: "Name, phone number, profile picture, and your default farm. The phone number drives SMS alerts for irrigation events when you're away from the dashboard.",
        },
        {
          type: "step",
          title: "Tighten login & security",
          text: "Change your password, enable two-factor authentication, and review active sessions. Recommended for operations where the same email is used by multiple devices.",
        },
        {
          type: "step",
          title: "Set notifications you care about",
          text: "Pick which events trigger a push, email, or SMS — irrigation overdue, fertilizer expiry, weekly AI report, weather warning, or a teammate completing a task.",
        },
        {
          type: "step",
          title: "Set Global preferences: language, theme, and timezone",
          text: "The Global preferences tile switches the interface between English and Arabic, light/dark theme, and timezone (Africa/Cairo by default). Area units (acres ↔ feddan) and currency (EGP ↔ USD) live one tile over in Farm Settings.",
        },
        {
          type: "step",
          title: "Manage payments and payouts",
          text: "Update the PayPal or card on file for your subscription, review past invoices, and download tax-ready PDFs. Farm Owners only.",
        },
        {
          type: "cta",
          title: "Open your settings",
          text: "Walk through the eight tiles and bring the platform in line with how you work.",
          button: "Open settings",
        },
      ],
    },
    ar: {
      title: "اضبط حسابك وتفضيلاتك",
      eyebrow: "الخطوة 5 من 15",
      blurb:
        "البيانات الشخصية، أمان الدخول، الإشعارات، اللغة، المدفوعات، وإعدادات المزرعة — كلها في مكان واحد.",
      imageAlt:
        "مخطط الإعدادات في FarmTopia يعرض ترويسة 'الحساب' باسم المستخدم وبريده، ثم ثماني بطاقات: البيانات الشخصية، أمان الدخول، المدفوعات، إعدادات المزرعة، الإشعارات، الخصوصية والمشاركة، التفضيلات العامة، والدعم والملاحظات.",
      body: [
        {
          type: "paragraph",
          text: "صفحة واحدة، ثماني بطاقات — البيانات الشخصية، أمان الدخول، المدفوعات، إعدادات المزرعة، الإشعارات، الخصوصية، اللغة، والدعم. كل ما تحتاجه ليصبح FarmTopia أداتك الشخصية.",
        },
        {
          type: "paragraph",
          text: "معظم المزارعين يزورون الإعدادات مرتين: مرة في اليوم الأول لضبط اللغة على العربية والعملة على الجنيه المصري، ومرة عند إضافة وسيلة دفع جديدة أو إدخال شريك. الصفحة مبنية لهاتين الرحلتين.",
        },
        {
          type: "step",
          title: "حدّث بياناتك الشخصية",
          text: "الاسم، رقم الهاتف، صورة الملف الشخصي، ومزرعتك الافتراضية. رقم الهاتف يقود تنبيهات SMS لأحداث الري عندما تكون بعيداً عن لوحة التحكم.",
        },
        {
          type: "step",
          title: "شدّد أمان الدخول",
          text: "غيّر كلمة المرور، فعّل المصادقة الثنائية، وراجع الجلسات النشطة. ينصح به للعمليات التي يستخدم فيها نفس البريد على أجهزة متعددة.",
        },
        {
          type: "step",
          title: "حدّد الإشعارات التي تهمك",
          text: "اختر الأحداث التي تُرسل إشعاراً (Push) أو بريداً أو SMS — تأخر الري، انتهاء صلاحية سماد، التقرير الأسبوعي، تحذير طقس، أو إنجاز زميل لمهمة.",
        },
        {
          type: "step",
          title: "اضبط التفضيلات العامة: اللغة والمظهر والمنطقة الزمنية",
          text: "بطاقة 'التفضيلات العامة' تبدّل الواجهة بين الإنجليزية والعربية، المظهر الفاتح/الداكن، والمنطقة الزمنية (أفريقيا/القاهرة افتراضياً). أما وحدات المساحة (فدان ↔ أكر) والعملة (جنيه ↔ دولار) فمكانها بطاقة 'إعدادات المزرعة' المجاورة.",
        },
        {
          type: "step",
          title: "أدر المدفوعات والإيصالات",
          text: "حدّث PayPal أو البطاقة المسجّلة للاشتراك، راجع الفواتير السابقة، ونزّل ملفات PDF للضرائب. متاح لمالك المزرعة فقط.",
        },
        {
          type: "cta",
          title: "افتح إعداداتك",
          text: "تفقّد البطاقات الثماني واجعل المنصة متوافقة مع طريقة عملك.",
          button: "افتح الإعدادات",
        },
      ],
    },
  },
  {
    slug: "finance",
    image: "/tutorial/finance.webp",
    en: {
      title: "Track revenue and expenses for every farm",
      eyebrow: "Step 6 of 15",
      blurb:
        "Wallet balance, monthly income, expense categories, and a 12-month account-balance chart, broken down per farm.",
      imageAlt:
        "FarmTopia finance overview wireframe showing four KPI cards (Wallet Balance, Monthly Income, Expenses Monthly, Budget), a Budget Overview list with category bars, a Recent Transactions table, an Account Balance line chart (planned vs actual across 12 months), and two pie charts for Income by Category and Expense by Category.",
      body: [
        {
          type: "paragraph",
          text: "Wallet balance, monthly income, monthly expenses, and your budget — all on the same page. Plus per-category breakdowns and a planned-vs-actual chart that keeps you honest.",
        },
        {
          type: "paragraph",
          text: "Finance is the page Egyptian co-owners ask about most often. It opens on an Overview tab, with separate Transactions and Budgets tabs for drill-down. The headline cards mirror the questions investors ask first: How much cash do we have, how much came in last month, how much went out, and is that on budget?",
        },
        {
          type: "step",
          title: "Pick the farm from the header",
          text: "Finance scopes to one farm at a time. Use the Farms dropdown at the top to switch — each farm has its own wallet, budget, and transaction history.",
        },
        {
          type: "step",
          title: "Read the four KPI tiles",
          text: "Wallet, Monthly Income, Expenses Monthly, and Budget. The +12.5% delta under each one compares to the previous month so you spot trends immediately.",
        },
        {
          type: "step",
          title: "Scan the budget overview",
          text: "Fertilizer, labor, crops, irrigation — each line shows budget consumed as a horizontal bar. Tap See more to open the full category breakdown for the season.",
        },
        {
          type: "step",
          title: "Review recent transactions",
          text: "Last 40 transactions with date, amount, category, and payment method. Red arrows are expenses, green arrows are income. Filter by category or date from the table header.",
        },
        {
          type: "step",
          title: "Compare planned vs actual",
          text: "The Account Balance chart overlays planned spending (purple) against actual (red) across 12 months. A widening gap is your early signal that the budget needs a revisit.",
        },
        {
          type: "cta",
          title: "Open finance for this farm",
          text: "Wallet, transactions, planned-vs-actual — everything in one page.",
          button: "Open finance",
        },
      ],
    },
    ar: {
      title: "تتبع الإيرادات والمصروفات لكل مزرعة",
      eyebrow: "الخطوة 6 من 15",
      blurb:
        "رصيد المحفظة، الدخل الشهري، تصنيفات المصروفات، ومخطط رصيد الحساب على مدار 12 شهراً — مفصّلة لكل مزرعة.",
      imageAlt:
        "مخطط نظرة عامة على المالية في FarmTopia يعرض أربع بطاقات مؤشرات (رصيد المحفظة، الدخل الشهري، المصروفات الشهرية، الميزانية)، نظرة عامة على الميزانية بأشرطة لكل تصنيف، جدول 'المعاملات الأخيرة'، مخطط خطي لرصيد الحساب (مخطط مقابل فعلي عبر 12 شهراً)، ومخططان دائريان للدخل بالتصنيف والمصروف بالتصنيف.",
      body: [
        {
          type: "paragraph",
          text: "رصيد المحفظة، الدخل الشهري، المصروفات الشهرية، والميزانية — كلها في نفس الصفحة. بالإضافة إلى تفصيل لكل تصنيف ومخطّط يقارن المُخطّط بالفعلي يكشف أي انحراف مبكراً.",
        },
        {
          type: "paragraph",
          text: "المالية هي الصفحة التي يسأل عنها الشركاء المصريون أكثر من غيرها. تفتح على تبويب 'نظرة عامة'، مع تبويبَي 'المعاملات' و'الميزانيات' للتفصيل. البطاقات الرئيسية تعكس الأسئلة الأولى للمستثمر: كم لدينا من سيولة، كم دخل الشهر الماضي، كم خرج، وهل هذا في حدود الميزانية؟",
        },
        {
          type: "step",
          title: "اختر المزرعة من الترويسة",
          text: "المالية تنحصر بمزرعة واحدة في كل مرة. استخدم قائمة Farms المنسدلة في الأعلى للتبديل — لكل مزرعة محفظتها وميزانيتها وتاريخ معاملاتها الخاص.",
        },
        {
          type: "step",
          title: "اقرأ بطاقات المؤشرات الأربع",
          text: "المحفظة، الدخل الشهري، المصروفات الشهرية، والميزانية. الفرق ‎+12.5%‎ تحت كل بطاقة يقارن بالشهر السابق لترى الاتجاه فوراً.",
        },
        {
          type: "step",
          title: "تصفّح نظرة الميزانية",
          text: "الأسمدة، العمالة، المحاصيل، الري — كل بند يعرض الميزانية المستهلكة كشريط أفقي. اضغط 'مزيد' لفتح التفصيل الكامل للتصنيفات في الموسم.",
        },
        {
          type: "step",
          title: "راجع المعاملات الأخيرة",
          text: "آخر 40 معاملة بالتاريخ والمبلغ والتصنيف ووسيلة الدفع. الأسهم الحمراء مصروفات، والأسهم الخضراء دخل. صفِّ بالتصنيف أو التاريخ من ترويسة الجدول.",
        },
        {
          type: "step",
          title: "قارن المخطّط بالفعلي",
          text: "مخطط رصيد الحساب يعرض الإنفاق المخطط (بنفسجي) مقابل الفعلي (أحمر) عبر 12 شهراً. اتساع الفجوة إشارة مبكرة لمراجعة الميزانية.",
        },
        {
          type: "cta",
          title: "افتح المالية لهذه المزرعة",
          text: "المحفظة، المعاملات، المخطّط مقابل الفعلي — كله في صفحة واحدة.",
          button: "افتح المالية",
        },
      ],
    },
  },
  {
    slug: "add-farm",
    image: "/tutorial/add-farm.webp",
    en: {
      title: "Add your first farm to the map",
      eyebrow: "Step 7 of 15",
      blurb:
        "Draw your farm's boundary directly on satellite imagery, set your water source, and FarmTopia starts tracking weather, NDVI, and irrigation for you.",
      imageAlt:
        "FarmTopia add-farm wireframe showing the form panel on the left with farm name and lat/long inputs, and a satellite map on the right with a magenta polygon outlining the farm.",
      body: [
        {
          type: "paragraph",
          text: "Draw your farm's boundary on satellite imagery, name it, pick your water source, and FarmTopia starts tracking weather, NDVI, and irrigation for that exact plot.",
        },
        {
          type: "paragraph",
          text: "Adding a farm takes about five minutes the first time. You'll need: the farm's location, an idea of its boundary corners (or just trace it on the satellite map), and your primary water source. Everything else can be filled in later.",
        },
        {
          type: "heading",
          text: "The five-minute walkthrough",
        },
        {
          type: "step",
          title: "Name your farm",
          text: "Pick something memorable — the village name + crop usually works (e.g. 'Mansoura wheat plot'). You can rename it later.",
        },
        {
          type: "step",
          title: "Set the boundary points",
          text: "Either click on the satellite map to drop boundary corners, or type lat/long coordinates directly. Most farms need 4–8 points; complex shapes can use more.",
        },
        {
          type: "step",
          title: "Validate the polygon",
          text: "FarmTopia checks the area is plausible (no self-intersecting boundaries, total area between 0.1 and 10,000 acres) and shows you the computed area in feddan and acres.",
        },
        {
          type: "step",
          title: "Pick a water source",
          text: "Choose a primary source (required) — Rain, River, Underground Water, or Treated Water — and optionally a secondary one. This drives the irrigation advisory: canal-rotation timing for river-fed farms differs from continuous-pressure systems.",
        },
        {
          type: "step",
          title: "Confirm the area and create the farm",
          text: "Check the auto-computed total area, then hit Create Farm. Irrigation parameters (rate, power type, cost per cubic metre) are no longer set here — you add those per-field in the Add Field wizard.",
        },
        {
          type: "heading",
          text: "Field tips",
        },
        {
          type: "tip",
          title: "Use satellite, not memory",
          text: "It's tempting to type coordinates from a notebook, but the satellite map is more accurate — especially around irregular field edges.",
        },
        {
          type: "tip",
          title: "Start small",
          text: "Add one farm first to learn the platform. You can bulk-import the rest from a KML or shapefile once you're comfortable.",
        },
        {
          type: "tip",
          title: "Water source matters",
          text: "If you irrigate from a canal, set the rotation schedule in the next step — that's what makes FarmTopia's irrigation advisories useful for Egyptian growers.",
        },
        {
          type: "cta",
          title: "Add your first farm",
          text: "You'll need under five minutes and the rough location of your plot.",
          button: "Open the add-farm wizard",
        },
      ],
    },
    ar: {
      title: "أضف أول مزرعة لك على الخريطة",
      eyebrow: "الخطوة 7 من 15",
      blurb:
        "ارسم حدود مزرعتك مباشرة على صور الأقمار الصناعية، حدد مصدر الماء، وستبدأ FarmTopia في متابعة الطقس وNDVI والري لقطعتك بالضبط.",
      imageAlt:
        "مخطط شاشة إضافة المزرعة في FarmTopia يعرض لوحة النموذج على اليسار باسم المزرعة وحقول الإحداثيات، وخريطة الأقمار على اليمين برسم وردي يحدد المزرعة.",
      body: [
        {
          type: "paragraph",
          text: "ارسم حدود مزرعتك على صور الأقمار، أعطها اسماً، اختر مصدر المياه، وستبدأ FarmTopia في متابعة الطقس وNDVI والري لتلك القطعة بالضبط.",
        },
        {
          type: "paragraph",
          text: "إضافة مزرعة تستغرق حوالي خمس دقائق في أول مرة. ستحتاج: موقع المزرعة، فكرة عن نقاط حدودها (أو ارسمها مباشرة على الخريطة)، ومصدر المياه الأساسي. كل شيء آخر يمكن إكماله لاحقاً.",
        },
        {
          type: "heading",
          text: "خطوات الخمس دقائق",
        },
        {
          type: "step",
          title: "أعطِ مزرعتك اسماً",
          text: "اختر اسماً يسهل تذكره — اسم القرية + المحصول عادةً ما يكفي (مثل 'حقل قمح المنصورة'). يمكنك تغييره لاحقاً.",
        },
        {
          type: "step",
          title: "حدد نقاط الحدود",
          text: "إما اضغط على خريطة الأقمار لإسقاط نقاط الحدود، أو اكتب الإحداثيات (خط طول/عرض) مباشرة. معظم المزارع تحتاج 4-8 نقاط؛ الأشكال المعقدة قد تستخدم أكثر.",
        },
        {
          type: "step",
          title: "تحقّق من المضلع",
          text: "تتحقق FarmTopia من أن المساحة معقولة (لا تتقاطع الحدود مع نفسها، إجمالي المساحة بين 0.1 و10,000 فدان) وتعرض المساحة المحسوبة بالفدان والأكر.",
        },
        {
          type: "step",
          title: "اختر مصدر المياه",
          text: "اختر مصدراً أساسياً (إلزامي) — مطر، نهر، مياه جوفية، أو مياه معالجة — ومصدراً ثانوياً اختيارياً. هذا يقود إرشاد الري: توقيت دورة الترعة للمزارع التي تروى من النهر يختلف عن أنظمة الضغط المستمر.",
        },
        {
          type: "step",
          title: "أكّد المساحة وأنشئ المزرعة",
          text: "راجع المساحة الإجمالية المحسوبة تلقائياً، ثم اضغط 'إنشاء المزرعة'. بيانات الري (المعدل، نوع الطاقة، التكلفة للمتر المكعب) لم تعد تُضبط هنا — تضيفها لكل حقل في معالج إضافة الحقل.",
        },
        {
          type: "heading",
          text: "نصائح ميدانية",
        },
        {
          type: "tip",
          title: "استخدم الأقمار وليس الذاكرة",
          text: "من المغري كتابة إحداثيات من دفتر، لكن خريطة الأقمار أدق — خاصة عند حواف الحقول غير المنتظمة.",
        },
        {
          type: "tip",
          title: "ابدأ بمزرعة واحدة",
          text: "أضف مزرعة واحدة أولاً لتتعلم المنصة. يمكنك استيراد الباقي دفعة واحدة من ملف KML أو shapefile بعد الإلمام.",
        },
        {
          type: "tip",
          title: "مصدر المياه مهم",
          text: "إذا كنت تروي من ترعة، حدد جدول الدورة في الخطوة التالية — هذا ما يجعل إرشادات الري في FarmTopia مفيدة للمزارع المصري.",
        },
        {
          type: "cta",
          title: "أضف أول مزرعة",
          text: "تحتاج أقل من خمس دقائق وموقع تقريبي لقطعتك.",
          button: "افتح معالج إضافة المزرعة",
        },
      ],
    },
  },
  {
    slug: "add-field",
    image: "/tutorial/add-field.webp",
    en: {
      title: "Subdivide a farm into managed fields",
      eyebrow: "Step 8 of 15",
      blurb:
        "Split a single farm into named fields with their own crop variety, area in feddan/acres, and independent irrigation plans.",
      imageAlt:
        "FarmTopia add-field wireframe showing the form on the left with field name, four lat/long input pairs, Add more points button, Validate your points button, total area, and Crop Type + Crop Variety dropdowns; satellite map on the right with a magenta field polygon. (The live flow is a fuller multi-step wizard — see the steps below.)",
      body: [
        {
          type: "paragraph",
          text: "A field is the smallest unit FarmTopia tracks independently. The Create-a-Field wizard captures its boundary, crop type and variety, sowing and harvest dates, soil and drainage, and optional irrigation and nutrition plans — so a 20-feddan wheat plot and the adjacent 5-feddan tomato plot are each managed on their own.",
        },
        {
          type: "paragraph",
          text: "Adding a field opens a guided multi-step wizard (Farms → your farm → Add field, titled 'Create a new field'). It goes further than Add Farm: beyond the boundary you set the crop, field properties (soil, drainage, zones), and optional irrigation and nutrition plans, with a review step at the end. On a typical 25-feddan Delta farm you'll end up with 3–5 fields.",
        },
        {
          type: "step",
          title: "Name the field",
          text: "Default is 'Field 1' or 'Crop Field'. Override with something you'll recognize on a phone in the field — 'North wheat' or 'Greenhouse 2' beats Field 7.",
        },
        {
          type: "step",
          title: "Draw the boundary and validate the area",
          text: "Click the satellite map to drop corners (four rows by default, with Add more points for irregular shapes) or type coordinates manually. Validate your points checks the geometry and computes the area in acres or feddan.",
        },
        {
          type: "step",
          title: "Set the crop: type, variety, and dates",
          text: "Crop type drives Kc values, variety drives growth-stage timing, and the sowing + expected-harvest dates anchor the FAO-56 curve. For Delta wheat, choosing 'Sids 14' uses the Sids 14 Kc curve, not the generic wheat one; tree crops also capture a tree count.",
        },
        {
          type: "step",
          title: "Set field properties",
          text: "The field-properties step captures soil type, drainage, irrigation type, number of zones, and notes — the inputs that tailor irrigation and fertilization to this specific field.",
        },
        {
          type: "step",
          title: "Add irrigation + nutrition plans, then review",
          text: "Optional irrigation-plan and nutrition-plan steps let you schedule water and fertilizer up front; a final review step confirms everything before the field is created, then links to the per-field crop dashboard.",
        },
        {
          type: "cta",
          title: "Add a field to a farm",
          text: "Open the Farms list, pick a farm, and add the field from inside it.",
          button: "Open my farms",
        },
      ],
    },
    ar: {
      title: "قسِّم المزرعة إلى حقول مُدارة",
      eyebrow: "الخطوة 8 من 15",
      blurb:
        "قسّم مزرعة واحدة إلى حقول مسمّاة بأنواعها وأصنافها ومساحاتها بالفدان/الأكر وخطط ري مستقلة.",
      imageAlt:
        "مخطط إضافة حقل في FarmTopia يعرض النموذج على اليسار بحقل اسم وأربعة أزواج من حقول الإحداثيات وزر 'إضافة المزيد من النقاط' وزر 'تحقّق من النقاط' وإجمالي المساحة وقوائم 'نوع المحصول' و'صنف المحصول'؛ خريطة الأقمار على اليمين برسم وردي للحقل. (المسار الحي معالج متعدّد الخطوات أكمل — انظر الخطوات أدناه.)",
      body: [
        {
          type: "paragraph",
          text: "الحقل هو أصغر وحدة تتابعها FarmTopia باستقلال. معالج 'إنشاء حقل' يلتقط حدوده ونوع محصوله وصنفه وتاريخي الزراعة والحصاد ونوع التربة والصرف وخطة ري وتسميد اختيارية — حتى تُدار كل قطعة على حدة، فلا تختلط 20 فداناً من القمح بـ 5 فدادين من الطماطم المجاورة.",
        },
        {
          type: "paragraph",
          text: "إضافة حقل تفتح معالجاً متعدّد الخطوات (المزارع ← مزرعتك ← إضافة حقل، بعنوان 'إنشاء حقل جديد'). يذهب أبعد من 'إضافة مزرعة': إلى جانب الحدود تضبط المحصول وخصائص الحقل (التربة، الصرف، المناطق) وخطط ري وتسميد اختيارية، مع خطوة مراجعة في النهاية. في مزرعة دلتاوية نموذجية بـ 25 فداناً ستنتهي بـ 3-5 حقول.",
        },
        {
          type: "step",
          title: "سمّ الحقل",
          text: "الاسم الافتراضي 'Field 1' أو 'Crop Field'. غيّره لاسم تتعرف عليه من الهاتف وأنت في الحقل — 'قمح الشمال' أو 'صوبة 2' أفضل من 'حقل 7'.",
        },
        {
          type: "step",
          title: "ارسم الحدود وتحقّق من المساحة",
          text: "اضغط على خريطة الأقمار لإسقاط النقاط (أربعة أسطر افتراضياً، مع 'إضافة المزيد من النقاط' للأشكال غير المنتظمة) أو اكتب الإحداثيات يدوياً. زر 'تحقّق من النقاط' يفحص الهندسة ويحسب المساحة بالأكر أو الفدان.",
        },
        {
          type: "step",
          title: "اضبط المحصول: النوع والصنف والتواريخ",
          text: "نوع المحصول يقود قيم Kc، والصنف يقود توقيت مراحل النمو، وتاريخا الزراعة والحصاد المتوقع يثبّتان منحنى FAO-56. لقمح الدلتا، اختيار 'سدس 14' يستخدم منحنى Kc لسدس 14 لا المنحنى العام للقمح؛ ومحاصيل الأشجار تلتقط عدد الأشجار أيضاً.",
        },
        {
          type: "step",
          title: "اضبط خصائص الحقل",
          text: "خطوة خصائص الحقل تلتقط نوع التربة والصرف ونوع الري وعدد المناطق والملاحظات — المدخلات التي تُفصّل الري والتسميد لهذا الحقل تحديداً.",
        },
        {
          type: "step",
          title: "أضف خطتي الري والتسميد ثم راجع",
          text: "خطوتا الري والتسميد الاختياريتان تتيحان جدولة الماء والسماد مسبقاً؛ وخطوة مراجعة أخيرة تؤكّد كل شيء قبل إنشاء الحقل، ثم ترتبط بلوحة بيانات المحصول لكل حقل.",
        },
        {
          type: "cta",
          title: "أضف حقلاً لمزرعة",
          text: "افتح قائمة المزارع، اختر مزرعة، وأضف الحقل من داخلها.",
          button: "افتح مزارعي",
        },
      ],
    },
  },
  {
    slug: "fertilizer",
    image: "/tutorial/fertilizer.webp",
    en: {
      title: "Run the fertilizer warehouse",
      eyebrow: "Step 9 of 15",
      blurb:
        "Track inventory, N-P-K ratios, planned vs unplanned usage, and AI-generated weekly fertilizer reports across every season.",
      imageAlt:
        "FarmTopia fertilizer wireframe showing summary KPI tiles, a Fertilizer Inventory table (name, available, planned vs unplanned stock, cost per unit, N-P-K ratio, type, last purchased, last used), a Purchase Records table, and a notification panel with AI Weekly Reports. (The live Fertilizer Warehouse shows four summary cards — Total Items, Total Value, Low Stock Alerts, Expiring Soon.)",
      body: [
        {
          type: "paragraph",
          text: "The Fertilizer Warehouse tracks every bag of urea, DAP, ammonium sulfate, and potassium sulfate by quantity, cost, N-P-K ratio (sulfur is captured too), and remainder — and keeps N-P-K-S totals in sync. Plus a season filter and AI-generated weekly reports that flag what to order next.",
        },
        {
          type: "paragraph",
          text: "On most Egyptian farms fertilizer is the largest non-labor expense. The Fertilizer Warehouse treats inventory like a warehouse — not a spreadsheet — so an assistant manager can answer 'how much DAP do we have left?' without walking out to the storage shed.",
        },
        {
          type: "step",
          title: "Read the four summary cards",
          text: "Total Items in stock, Total Value of inventory, Low Stock Alerts, and items Expiring Soon — the four cards at the top of the Fertilizer Warehouse.",
        },
        {
          type: "step",
          title: "Browse the inventory table",
          text: "One row per fertilizer with planned vs unplanned stock, cost per kg in EGP, the N-P-K ratio shown per item (e.g. 46-0-0 for urea; sulfur is captured in the add-item form too), type, last purchase, and last use.",
        },
        {
          type: "step",
          title: "Filter by season",
          text: "The Season 2023–2024 dropdown narrows the table to a specific crop cycle. Useful when planning next season's purchases against last season's actuals.",
        },
        {
          type: "step",
          title: "Log a purchase or add a new fertilizer",
          text: "Add new Fertilizer opens the form. Enter name, quantity, cost per kg, expiry date, and supplier. The purchase shows up in the Purchase Records table below.",
        },
        {
          type: "step",
          title: "Read the AI Weekly Report",
          text: "Every Monday Hathor generates a fertilizer report from your inventory + crop calendar + weather. It flags low stock, suggests bulk-buy timing, and warns about expiring bags.",
        },
        {
          type: "cta",
          title: "Open the fertilizer warehouse",
          text: "Pick a farm from the Farms list to see its full inventory and purchase history.",
          button: "Open my farms",
        },
      ],
    },
    ar: {
      title: "شغّل مستودع الأسمدة",
      eyebrow: "الخطوة 9 من 15",
      blurb:
        "تتبع المخزون ونسب N-P-K والاستهلاك المخطّط مقابل غير المخطّط وتقارير أسبوعية مولَّدة بالذكاء الاصطناعي لكل موسم.",
      imageAlt:
        "مخطط الأسمدة في FarmTopia يعرض بطاقات مؤشرات، جدول مخزون الأسمدة بأعمدة للاسم والمتاح والمخطط مقابل غير المخطط والتكلفة لكل وحدة ونسبة N-P-K والنوع وآخر شراء وآخر استخدام؛ جدول 'سجلات الشراء'؛ ولوحة تنبيهات بتقارير الذكاء الاصطناعي الأسبوعية. (مستودع الأسمدة الحي يعرض أربع بطاقات: إجمالي العناصر، إجمالي القيمة، تنبيهات انخفاض المخزون، قرب انتهاء الصلاحية.)",
      body: [
        {
          type: "paragraph",
          text: "مستودع الأسمدة يتتبّع كل شيكارة يوريا، DAP، سلفات أمونيوم، وسلفات بوتاسيوم بالكمية والتكلفة ونسبة N-P-K (والكبريت يُسجَّل أيضاً) والمتبقي — ويبقي إجماليات N-P-K-S متّسقة. مع منتقي موسم وتقارير أسبوعية مولّدة بالذكاء الاصطناعي تنبّهك بما يجب طلبه التالي.",
        },
        {
          type: "paragraph",
          text: "في معظم المزارع المصرية، الأسمدة هي أكبر مصروف بعد العمالة. مستودع الأسمدة يعامل المخزون كمستودع — لا كجدول بيانات — حتى يستطيع مساعد المدير الإجابة عن 'كم تبقّى من DAP؟' دون النزول إلى مخزن السماد.",
        },
        {
          type: "step",
          title: "اقرأ البطاقات الأربع",
          text: "إجمالي العناصر في المخزون، إجمالي قيمة المخزون، تنبيهات انخفاض المخزون، والعناصر قاربة انتهاء الصلاحية — البطاقات الأربع أعلى مستودع الأسمدة.",
        },
        {
          type: "step",
          title: "تصفّح جدول المخزون",
          text: "سطر لكل سماد بالمخزون المخطط مقابل غير المخطط، التكلفة لكل كيلو بالجنيه المصري، نسبة N-P-K المعروضة لكل عنصر (مثل 46-0-0 لليوريا؛ والكبريت يُسجَّل في نموذج الإضافة أيضاً)، النوع، آخر شراء، وآخر استخدام.",
        },
        {
          type: "step",
          title: "صفِّ بالموسم",
          text: "قائمة 'الموسم 2023-2024' المنسدلة تحصر الجدول في دورة محصولية محددة. مفيدة عند تخطيط مشتريات الموسم القادم استناداً إلى أرقام الموسم السابق.",
        },
        {
          type: "step",
          title: "سجّل عملية شراء أو أضف سماداً جديداً",
          text: "زر 'إضافة سماد جديد' يفتح النموذج. أدخل الاسم والكمية والتكلفة لكل كيلو وتاريخ الانتهاء والمورد. عملية الشراء تظهر في جدول 'سجلات الشراء' أسفل.",
        },
        {
          type: "step",
          title: "اقرأ التقرير الأسبوعي للذكاء الاصطناعي",
          text: "كل يوم إثنين، تولّد حتحور تقرير أسمدة من مخزونك + تقويم المحاصيل + الطقس. يُنبّهك بالمخزون المنخفض، ويقترح توقيت الشراء بالجملة، ويحذّر من الشيكارات قاربت الانتهاء.",
        },
        {
          type: "cta",
          title: "افتح مستودع الأسمدة",
          text: "اختر مزرعة من قائمة Farms لرؤية كامل المخزون وسجل الشراء.",
          button: "افتح مزارعي",
        },
      ],
    },
  },
  {
    slug: "irrigation",
    image: "/tutorial/irrigation.webp",
    en: {
      title: "Plan smart irrigation with Kc and canal timing",
      eyebrow: "Step 10 of 15",
      blurb:
        "Daily water demand from FAO-56 Kc × weather, with canal-rotation timing baked in. Hathor flags fields that need attention.",
      imageAlt:
        "FarmTopia irrigation farm wireframe showing four performance metrics (Daily Total Water Arrival, Expected Total Water Cost, Expected Total Energy Cost, Expected Total Energy Consumption), a Fields That Need Attention carousel with a 39% completion ring on Field 5, a Hathor Recommendations card, and an Irrigation System Parameters panel with water-source details (River, water pH, EC, temperature) and inputs for irrigation rate, power type, cost per liter, and energy per liter.",
      body: [
        {
          type: "paragraph",
          text: "Daily water demand calculated from FAO-56 Kc curves × today's ET₀ × your field's area, with canal-rotation timing baked in. The page shows which fields need attention and what Hathor recommends.",
        },
        {
          type: "paragraph",
          text: "Irrigation is the page that justifies the Premium plan for most growers. The Egyptian canal-rotation calendar (the 'dawran' / دوران) means you can't just water on demand — you water when the water arrives. FarmTopia plans around that.",
        },
        {
          type: "step",
          title: "Read the performance metrics",
          text: "Daily water arrival, water cost, energy cost, and energy consumption — the four numbers that tell you whether today's irrigation is on budget.",
        },
        {
          type: "step",
          title: "Triage Fields That Need Attention",
          text: "Each field shows a completion ring and a status line. 'Irrigation Plan Incomplete' (Field 5 at 39%) means a stage is missing — click Complete Irrigation Plan to fix it before the next watering.",
        },
        {
          type: "step",
          title: "Read Hathor's recommendation",
          text: "Hathor compares today's ET₀ against last week, checks if your irrigation efficiency is dropping, and proposes a schedule adjustment. Use View Problem Areas to see which sub-zones are starved.",
        },
        {
          type: "step",
          title: "Set water-source parameters",
          text: "For canal-fed farms, enter pH, EC, and temperature for your primary source (river by default). These feed the Kc × ET₀ calculation — saline water needs more flushing irrigation.",
        },
        {
          type: "step",
          title: "Tune irrigation system inputs",
          text: "Total irrigation rate in liters/hour, power type (solar-powered system in the demo), cost per cubic metre, and energy per liter. Drives the daily cost forecast at the top of the page.",
        },
        {
          type: "cta",
          title: "Open the irrigation planner",
          text: "See today's plan, fields needing attention, and Hathor's recommendation.",
          button: "Open irrigation",
        },
      ],
    },
    ar: {
      title: "خطط للري الذكي مع Kc وتوقيت الترعة",
      eyebrow: "الخطوة 10 من 15",
      blurb:
        "احتياج الماء اليومي محسوب من FAO-56 Kc × الطقس × توقيت دورة الترعة. وحتحور تُنبّهك للحقول التي تحتاج تدخّلاً.",
      imageAlt:
        "مخطط ري المزرعة في FarmTopia يعرض أربعة مقاييس أداء (إجمالي الماء اليومي الواصل، التكلفة الإجمالية المتوقعة للماء، التكلفة الإجمالية المتوقعة للطاقة، الاستهلاك الإجمالي المتوقع للطاقة)، شريط 'حقول تحتاج اهتماماً' بحلقة إتمام 39% على الحقل 5، بطاقة 'توصيات حتحور'، ولوحة 'بيانات نظام الري' بتفاصيل مصدر الماء (نهر، pH الماء، EC، درجة الحرارة) وحقول إدخال لمعدل الري ونوع الطاقة والتكلفة لكل لتر والطاقة لكل لتر.",
      body: [
        {
          type: "paragraph",
          text: "احتياج الماء اليومي محسوب من منحنيات FAO-56 Kc × قيمة ET₀ اليوم × مساحة حقلك، مع توقيت دورة الترعة في الحساب. الصفحة تعرض الحقول التي تحتاج تدخّلاً وما توصي به حتحور.",
        },
        {
          type: "paragraph",
          text: "الري هي الصفحة التي تبرّر الباقة الممتازة لمعظم المزارعين. تقويم دورة الترعة (الدوران) في مصر يعني أنك لا تستطيع الري عند الطلب — تروي عند وصول الماء. FarmTopia تخطط مع هذا الواقع.",
        },
        {
          type: "step",
          title: "اقرأ مقاييس الأداء",
          text: "إجمالي الماء اليومي، تكلفة الماء، تكلفة الطاقة، واستهلاك الطاقة — الأرقام الأربعة التي تخبرك ما إذا كان ري اليوم في حدود الميزانية.",
        },
        {
          type: "step",
          title: "رتّب الحقول التي تحتاج اهتماماً",
          text: "كل حقل يعرض حلقة إتمام وسطر حالة. 'خطة ري غير مكتملة' (الحقل 5 عند 39%) تعني أن مرحلة مفقودة — اضغط 'إكمال خطة الري' لإصلاحها قبل الرية القادمة.",
        },
        {
          type: "step",
          title: "اقرأ توصية حتحور",
          text: "حتحور تقارن ET₀ اليوم بالأسبوع الماضي، تفحص ما إذا كانت كفاءة الري تنخفض، وتقترح تعديل الجدول. استخدم 'عرض مناطق المشاكل' لرؤية المناطق الفرعية الجائعة للماء.",
        },
        {
          type: "step",
          title: "اضبط بيانات مصدر الماء",
          text: "للمزارع التي تروى من ترعة، أدخل pH وEC ودرجة الحرارة لمصدرك الأساسي (نهر افتراضياً). هذه تغذي حساب Kc × ET₀ — الماء المالح يحتاج ري غسيل إضافي.",
        },
        {
          type: "step",
          title: "اضبط مدخلات نظام الري",
          text: "إجمالي معدل الري باللتر/الساعة، نوع الطاقة (نظام شمسي في النموذج)، التكلفة للمتر المكعب، والطاقة لكل لتر. تقود توقع التكلفة اليومية في أعلى الصفحة.",
        },
        {
          type: "cta",
          title: "افتح مخطط الري",
          text: "اطّلع على خطة اليوم والحقول التي تحتاج اهتماماً وتوصية حتحور.",
          button: "افتح الري",
        },
      ],
    },
  },
  {
    slug: "report",
    image: "/tutorial/report.webp",
    en: {
      title: "Generate AI reports across your farm",
      eyebrow: "Step 11 of 15",
      blurb:
        "Weather, crop, fertilizer, finance, and team reports — some authored by your team, some generated by AI from your live data.",
      imageAlt:
        "FarmTopia Reports wireframe with four tabs (All / Team / AI / Bookmarked), a Create Report button, a hero headline 'Farm Insights: Comprehensive Reports for Smarter Decisions', and category cards for Weather (Weather Impact Analysis, Seasonal Weather Summary, Climate Data Insights), Crop (Field Health Monitoring Report, Pest Surveillance Data, Crop Disease Trend Analysis), Fertilizer (Fertilizer Usage Report, Nutrient Application Summary), and Finance (Farm Financial Overview, Profit and Loss Report).",
      body: [
        {
          type: "paragraph",
          text: "The page is titled 'Reports Overview' and spans many categories — weather, crop, irrigation, fertilizer, finance, disease, harvest, and more — across four tabs: All, Team, AI, and Bookmarked. Some reports are authored by your team; the AI tab covers reports generated from your live farm data.",
        },
        {
          type: "paragraph",
          text: "Reports is the page co-owners and investors visit during monthly reviews. Everything is exportable to PDF, and the AI reports cite the data they're built on so you can drill into the underlying numbers when something looks off.",
        },
        {
          type: "step",
          title: "Pick a tab: All, Team, AI, or Bookmarked",
          text: "All shows everything. Team is the reports your staff authored. AI is the ones generated automatically from your farm data. Bookmarked is where the reports you've pinned for quick access live.",
        },
        {
          type: "step",
          title: "Browse by category",
          text: "Categories like weather, crop, irrigation, fertilizer, finance, disease, and harvest — each card lists several reports. AI-generated reports are tagged with a small 'AI' badge; new ones get a 'New' badge for a week.",
        },
        {
          type: "step",
          title: "Open a report and read the citations",
          text: "AI reports show the date ranges and data sources they consulted (e.g. 'Sentinel-2 NDVI from 2026-04-01 to 2026-04-30' for a Field Health Monitoring Report). Click any source to drill in.",
        },
        {
          type: "step",
          title: "Create a custom report",
          text: "Create Report opens the builder. Pick the data set, the date range, the farm/field scope, and whether to generate the narrative with Hathor. Saves as a reusable template.",
        },
        {
          type: "step",
          title: "Comment and bookmark",
          text: "Use the comment count badge to read threaded notes from your team, and the bookmark icon to pin a report to your dashboard. Useful for the recurring monthly review.",
        },
        {
          type: "cta",
          title: "Browse all reports",
          text: "See AI-generated weekly reports and the ones your team has authored, side by side.",
          button: "Open reports",
        },
      ],
    },
    ar: {
      title: "ولّد تقارير الذكاء الاصطناعي لمزرعتك",
      eyebrow: "الخطوة 11 من 15",
      blurb:
        "تقارير الطقس والمحاصيل والأسمدة والمالية والفريق — بعضها يكتبها فريقك وأكثرها يولّده الذكاء الاصطناعي من بياناتك الحية.",
      imageAlt:
        "مخطط التقارير في FarmTopia بأربعة تبويبات (الكل / الفريق / الذكاء الاصطناعي / المحفوظة)، زر 'إنشاء تقرير'، عنوان رئيسي 'رؤى المزرعة: تقارير شاملة لقرارات أذكى'، وبطاقات تصنيفات للطقس (تحليل تأثير الطقس، ملخص الطقس الموسمي، رؤى بيانات المناخ)، المحصول (تقرير مراقبة صحة الحقل، بيانات مراقبة الآفات، تحليل اتجاه أمراض المحاصيل)، الأسمدة (تقرير استخدام الأسمدة، ملخص تطبيق المغذيات)، والمالية (نظرة عامة مالية للمزرعة، تقرير الأرباح والخسائر).",
      body: [
        {
          type: "paragraph",
          text: "الصفحة بعنوان 'نظرة عامة على التقارير' وتغطي تصنيفات عديدة — الطقس، المحصول، الري، الأسمدة، المالية، الأمراض، الحصاد، وغيرها — عبر أربعة تبويبات: الكل، الفريق، الذكاء الاصطناعي، والمحفوظة. بعض التقارير يكتبها فريقك؛ وتبويب الذكاء الاصطناعي يغطي التقارير المولّدة من بيانات مزرعتك الحية.",
        },
        {
          type: "paragraph",
          text: "التقارير هي الصفحة التي يزورها الشركاء والمستثمرون في المراجعات الشهرية. كل شيء قابل للتصدير بصيغة PDF، وتقارير الذكاء الاصطناعي تُشير إلى البيانات التي بُنيت عليها لتتمكن من التعمّق في الأرقام عند ظهور شيء غريب.",
        },
        {
          type: "step",
          title: "اختر تبويباً: الكل، الفريق، الذكاء الاصطناعي، أو المحفوظة",
          text: "'الكل' يعرض كل شيء. 'الفريق' هو ما كتبه طاقمك. 'الذكاء الاصطناعي' ما يُولَّد تلقائياً من بيانات مزرعتك. 'المحفوظة' حيث تجد التقارير التي ثبّتها للوصول السريع.",
        },
        {
          type: "step",
          title: "تصفّح بالتصنيف",
          text: "تصنيفات مثل الطقس والمحصول والري والأسمدة والمالية والأمراض والحصاد — كل بطاقة تعرض عدة تقارير. تقارير الذكاء الاصطناعي بشارة 'AI' صغيرة؛ الجديدة منها بشارة 'New' لمدة أسبوع.",
        },
        {
          type: "step",
          title: "افتح تقريراً واقرأ المصادر",
          text: "تقارير الذكاء الاصطناعي تعرض النطاقات الزمنية ومصادر البيانات التي استشارتها (مثل 'NDVI من Sentinel-2 من 2026-04-01 إلى 2026-04-30' لتقرير مراقبة صحة الحقل). اضغط على أي مصدر للتعمّق.",
        },
        {
          type: "step",
          title: "أنشئ تقريراً مخصصاً",
          text: "زر 'إنشاء تقرير' يفتح المُنشئ. اختر مجموعة البيانات والنطاق الزمني ونطاق المزرعة/الحقل وما إذا كنت تريد توليد السرد بـ حتحور. يُحفظ كقالب لإعادة الاستخدام.",
        },
        {
          type: "step",
          title: "اكتب تعليقاً أو احفظ مرجعاً",
          text: "استخدم شارة عدد التعليقات لقراءة الملاحظات المتسلسلة من فريقك، وأيقونة الإشارة المرجعية لتثبيت تقرير على لوحة التحكم. مفيد للمراجعة الشهرية المتكررة.",
        },
        {
          type: "cta",
          title: "تصفّح كل التقارير",
          text: "اطّلع على تقارير الذكاء الاصطناعي الأسبوعية وما كتبه فريقك جنباً إلى جنب.",
          button: "افتح التقارير",
        },
      ],
    },
  },
  {
    slug: "task",
    image: "/tutorial/task.webp",
    en: {
      title: "Assign and complete field tasks",
      eyebrow: "Step 12 of 15",
      blurb:
        "Create tasks, scope them to a whole farm or a single field, assign a worker, and track progress until the task is done.",
      imageAlt:
        "FarmTopia Tasks Overview wireframe showing a search bar, three filter dropdowns (Status, Date, Category), a view toggle, four KPI tiles (Total Tasks 13, Pending 5, Scheduled 8, Completed 3), a three-column Kanban board (Upcoming, In Progress, Completed) with task cards showing TASK-001 Fertilization — Farm A, Low priority, Field B, May 26 9:00 AM, assigned to James Wilson and a progress bar at 44%; a Define Task Scope modal overlay asks the user to choose Entire Farm or Specific Field for the new task.",
      body: [
        {
          type: "paragraph",
          text: "Create a task, scope it to the whole farm or a specific field, set status (pending / scheduled / completed), assign a worker, and track progress as the team works through it.",
        },
        {
          type: "paragraph",
          text: "Tasks is the page workers open from their phone every morning. The Kanban layout — Upcoming, Scheduled, Completed — maps to how field crews actually think about their day. Adding a task takes three taps.",
        },
        {
          type: "step",
          title: "Open Tasks Overview",
          text: "From any farm screen, click Tasks. The KPI strip at the top gives you the count by status — 13 total, 5 pending, 8 scheduled, 3 completed in the demo.",
        },
        {
          type: "step",
          title: "Filter and search",
          text: "Use Status, Date, Category filters or the search box to find a specific task. Toggle between list view and the three-column Kanban from the top-right icons.",
        },
        {
          type: "step",
          title: "Add a task and pick the scope",
          text: "Click Add Task. The Define Task Scope modal asks 'Entire Farm or Specific Field?'. Pick Entire Farm for general work (e.g. inventory), Specific Field for spraying or fertilization.",
        },
        {
          type: "step",
          title: "Fill in the task details",
          text: "Title, description, priority (Low/Medium/High), category (Fertilization / Irrigation / Harvest / Inspection / Other), scheduled date, and the assignee from your team.",
        },
        {
          type: "step",
          title: "Track progress to completion",
          text: "The assignee updates the progress bar from their phone. A task moves from Upcoming to In Progress to Completed automatically as the bar fills. Worker comments are timestamped for audit.",
        },
        {
          type: "cta",
          title: "Open your task board",
          text: "Pick a farm and walk through today's pending and scheduled tasks.",
          button: "Open my farms",
        },
      ],
    },
    ar: {
      title: "وزّع وأنجز مهام الحقل",
      eyebrow: "الخطوة 12 من 15",
      blurb:
        "أنشئ مهمة، حدّد نطاقها على مزرعة كاملة أو حقل واحد، عيّن عاملاً، وتابع التقدّم حتى الإنجاز.",
      imageAlt:
        "مخطط نظرة عامة على المهام في FarmTopia يعرض شريط بحث، ثلاث قوائم تصفية (الحالة، التاريخ، التصنيف)، مفتاح عرض، أربع بطاقات مؤشرات (إجمالي المهام 13، قيد الانتظار 5، مجدولة 8، مكتملة 3)، لوحة Kanban بثلاثة أعمدة (قادمة، قيد التنفيذ، مكتملة) ببطاقات مهام تعرض TASK-001 تسميد - مزرعة A، أولوية منخفضة، حقل B، 26 مايو 9:00 ص، موكّل لجيمس ويلسون وشريط تقدّم بـ 44%؛ نافذة 'تحديد نطاق المهمة' المنبثقة تطلب من المستخدم اختيار 'مزرعة كاملة' أو 'حقل بعينه' للمهمة الجديدة.",
      body: [
        {
          type: "paragraph",
          text: "أنشئ مهمة، حدّد نطاقها على المزرعة كاملة أو على حقل بعينه، اضبط الحالة (قيد الانتظار / مجدولة / مكتملة)، عيّن عاملاً، وتابع التقدّم بينما يعمل الفريق عليها.",
        },
        {
          type: "paragraph",
          text: "المهام هي الصفحة التي يفتحها العمال من هواتفهم كل صباح. تخطيط Kanban — قادمة، مجدولة، مكتملة — يعكس كيف يفكر طاقم الحقل في يومه. إضافة مهمة تستغرق ثلاث ضغطات.",
        },
        {
          type: "step",
          title: "افتح نظرة المهام",
          text: "من أي شاشة مزرعة، اضغط 'Tasks'. شريط المؤشرات في الأعلى يعطيك العدد بالحالة — 13 إجمالاً، 5 قيد الانتظار، 8 مجدولة، 3 مكتملة في النموذج.",
        },
        {
          type: "step",
          title: "صفِّ وابحث",
          text: "استخدم مرشحات الحالة والتاريخ والتصنيف أو مربع البحث للعثور على مهمة بعينها. انتقل بين العرض القائمة وKanban بثلاثة أعمدة من أيقونات الزاوية اليمنى العلوية.",
        },
        {
          type: "step",
          title: "أضف مهمة واختر نطاقها",
          text: "اضغط 'إضافة مهمة'. النافذة 'تحديد نطاق المهمة' تسأل 'مزرعة كاملة أم حقل بعينه؟'. اختر 'مزرعة كاملة' للعمل العام (مثل الجرد)، و'حقل بعينه' للرش أو التسميد.",
        },
        {
          type: "step",
          title: "املأ تفاصيل المهمة",
          text: "العنوان، الوصف، الأولوية (منخفضة/متوسطة/عالية)، التصنيف (تسميد / ري / حصاد / فحص / آخر)، التاريخ المجدول، والمكلَّف من فريقك.",
        },
        {
          type: "step",
          title: "تابع التقدّم حتى الإنجاز",
          text: "المكلَّف يحدّث شريط التقدّم من هاتفه. تتحرك المهمة من 'قادمة' إلى 'قيد التنفيذ' إلى 'مكتملة' تلقائياً بينما يمتلئ الشريط. تعليقات العامل مؤرّخة للتدقيق.",
        },
        {
          type: "cta",
          title: "افتح لوحة مهامك",
          text: "اختر مزرعة وتفقّد مهام اليوم القيد الانتظار والمجدولة.",
          button: "افتح مزارعي",
        },
      ],
    },
  },
  {
    slug: "hathor-ai",
    image: "/tutorial/hathor-ai.webp",
    en: {
      title: "Ask Hathor, the AI agronomist",
      eyebrow: "Step 13 of 15",
      blurb:
        "A chatbot that knows your farm, your weather, and your crop — ready with fertilizer plans, irrigation tips, and pest advice.",
      imageAlt:
        "FarmTopia farm dashboard wireframe with the Hathor chatbot panel expanded in the bottom-left. Header reads 'FarmTopiaBot' with online status; first message says 'Hello! I'm Hathor, your AI assistant for FarmTopia. I see you're growing wheat on your 10-acre farm. How can I help you today?'; four quick-reply chips below — Crop Information & Tips, Fertilizer Recommendations, Irrigation Tips, Something Else; user message 'Hi Hathor, I need information about the wheat I'm growing'; Ask your question input at the bottom; and a green Chat with Hathor button in the farm sidebar.",
      body: [
        {
          type: "paragraph",
          text: "Hathor is FarmTopia's chatbot for crop questions — it sees your farm, your current weather, your sown crop, and your inventory, and answers in plain Arabic or English.",
        },
        {
          type: "paragraph",
          text: "Named after the Egyptian goddess of agriculture, Hathor sits in a green chat bubble at the bottom of every farm page. Tap it once and you're talking to a model that already knows you're growing wheat on 10 feddan in Farafra — you don't have to re-explain.",
        },
        {
          type: "step",
          title: "Open Hathor from any farm page",
          text: "Click the green 'Chat with Hathor' button — visible on Farms, Fields, Weather, Irrigation, and Finance pages. The chat opens as a panel, not a new tab, so your farm context stays on screen.",
        },
        {
          type: "step",
          title: "Read the personalized greeting",
          text: "Hathor's first message already knows your farm name, area in feddan/acres, and the crop you've sown. Saves you the awkward 'I have a 10-feddan wheat farm in Farafra' setup.",
        },
        {
          type: "step",
          title: "Use the quick-reply chips",
          text: "Four pre-built prompts — Crop Information & Tips, Fertilizer Recommendations, Irrigation Tips, Something Else. Each one routes to a specialized prompt that pulls the right data from your farm.",
        },
        {
          type: "step",
          title: "Ask a free-form question in Arabic or English",
          text: "Type in the Ask your question box. Hathor handles both Arabic and English fluently and cites the ARC bulletin or FAO-56 reference when it gives an agronomic answer.",
        },
        {
          type: "step",
          title: "Act on the recommendation",
          text: "When Hathor suggests a fertilizer dose or an irrigation schedule, it surfaces an Apply button. One tap creates the matching task on your task board, assigned to you for review.",
        },
        {
          type: "cta",
          title: "Meet Hathor",
          text: "Open the AI landing page to see what Hathor can do before you commit to a Premium plan.",
          button: "Open AI landing",
        },
      ],
    },
    ar: {
      title: "اسأل حتحور، الخبير الزراعي بالذكاء الاصطناعي",
      eyebrow: "الخطوة 13 من 15",
      blurb:
        "شات بوت يعرف مزرعتك وطقسك ومحصولك — جاهز بخطط التسميد ونصائح الري والإرشاد ضد الآفات.",
      imageAlt:
        "مخطط لوحة تحكم المزرعة في FarmTopia مع لوحة شات حتحور موسّعة في الأسفل لليسار. الترويسة 'FarmTopiaBot' بحالة 'متصل'؛ أول رسالة 'مرحباً! أنا حتحور، مساعدتك بالذكاء الاصطناعي لـ FarmTopia. أرى أنك تزرع القمح على مزرعتك بـ 10 أكر. كيف أساعدك اليوم؟'؛ أربع شارات ردود سريعة تحتها — معلومات ونصائح عن المحصول، توصيات الأسمدة، نصائح الري، شيء آخر؛ رسالة المستخدم 'مرحباً حتحور، أحتاج معلومات عن القمح الذي أزرعه'؛ مربع 'اطرح سؤالك' في الأسفل؛ وزر 'تحدث مع حتحور' الأخضر في شريط جانب المزرعة.",
      body: [
        {
          type: "paragraph",
          text: "حتحور هي شات بوت FarmTopia لأسئلة المحاصيل — ترى مزرعتك وطقسك الحالي والمحصول المزروع ومخزونك، وتجيب بالعربية أو الإنجليزية بوضوح.",
        },
        {
          type: "paragraph",
          text: "سُمّيت تيمناً بإلهة الزراعة المصرية، وتجلس حتحور في فقاعة محادثة خضراء أسفل كل صفحة مزرعة. اضغطها مرة واحدة وتجد نفسك تتحدث إلى نموذج يعرف مسبقاً أنك تزرع قمحاً على 10 فدان في الفرافرة — لا تحتاج لإعادة شرح ذلك.",
        },
        {
          type: "step",
          title: "افتح حتحور من أي صفحة مزرعة",
          text: "اضغط الزر الأخضر 'تحدث مع حتحور' — يظهر في صفحات Farms وFields وWeather وIrrigation وFinance. المحادثة تفتح كلوحة، لا كنافذة جديدة، حتى يبقى سياق مزرعتك على الشاشة.",
        },
        {
          type: "step",
          title: "اقرأ التحية الشخصية",
          text: "رسالة حتحور الأولى تعرف اسم مزرعتك ومساحتها بالفدان/الأكر والمحصول الذي زرعته. توفّر عليك مقدمة 'لدي مزرعة قمح 10 فدان في الفرافرة' المحرجة.",
        },
        {
          type: "step",
          title: "استخدم شارات الردود السريعة",
          text: "أربعة أسئلة جاهزة — معلومات ونصائح عن المحصول، توصيات الأسمدة، نصائح الري، شيء آخر. كل شارة تذهب إلى Prompt متخصص يجلب البيانات المناسبة من مزرعتك.",
        },
        {
          type: "step",
          title: "اطرح سؤالاً حراً بالعربية أو الإنجليزية",
          text: "اكتب في مربع 'اطرح سؤالك'. حتحور تتعامل مع العربية والإنجليزية بطلاقة وتُشير إلى نشرة مركز البحوث الزراعية أو مرجع FAO-56 عند تقديم إجابة زراعية.",
        },
        {
          type: "step",
          title: "نفّذ التوصية",
          text: "عندما تقترح حتحور جرعة سماد أو جدول ري، يظهر زر 'تطبيق'. ضغطة واحدة تُنشئ المهمة المقابلة على لوحة مهامك، موكَّلة إليك للمراجعة.",
        },
        {
          type: "cta",
          title: "تعرّف على حتحور",
          text: "افتح صفحة الذكاء الاصطناعي لرؤية ما تستطيع حتحور فعله قبل الاشتراك في الباقة الممتازة.",
          button: "افتح صفحة الذكاء الاصطناعي",
        },
      ],
    },
  },
  {
    slug: "crop-data",
    image: "/tutorial/crop-data.webp",
    en: {
      title: "Open the crop wiki for your field",
      eyebrow: "Step 14 of 15",
      blurb:
        "Per-field crop dashboard: FAO-56 Kc, NDVI, LAI, satellite analysis, fertilization targets, and irrigation status in one screen.",
      imageAlt:
        "FarmTopia field detail wireframe with a top tab strip (Field one, Field two selected, Field three, see more fields), a breadcrumb (Home > Fields > Field two), a hero card 'Field 1 — 50 Acres, Farafra, western egypt, Government-Owned', then sections for Crop Basic Information, Crop Health Metrics with a View Full Crop Analysis link, Weather, Satellite Analysis Widgets, and Operational Controls for Irrigation and Fertilization with a Total Irrigation Health bar at 20%. Satellite imagery on the right with a magenta field polygon.",
      body: [
        {
          type: "paragraph",
          text: "The per-field crop dashboard surfaces basic crop info (seeding date, expected harvest, planned spend), live health metrics (NDVI, LAI, Kcb, total performance), weather, and operational controls for irrigation and fertilization — all on one screen.",
        },
        {
          type: "paragraph",
          text: "This is the page agronomists open most often. Everything FarmTopia knows about a single field, organized like a wiki entry: identity at the top, vitals in the middle, controls at the bottom. Pair it with the satellite map on the right and you can run a field visit without leaving your desk.",
        },
        {
          type: "step",
          title: "Switch between your fields",
          text: "The top tab strip lists every field on this farm. 'see more fields' opens the full picker if you have more than four. The breadcrumb (Home > Fields > Field two) doubles as a quick-back navigation.",
        },
        {
          type: "step",
          title: "Read the identity card",
          text: "Field name, area in acres, location, and ownership tag (Government-Owned vs Private). The edit icon in the corner lets you fix the area or rename the field without going back to the Add Field flow.",
        },
        {
          type: "step",
          title: "Scan crop basic information",
          text: "Seeding date, expected harvest, planned spending, expected yield, total spending so far. The dates feed the FAO-56 Kc curve so the irrigation page knows what growth stage you're in.",
        },
        {
          type: "step",
          title: "Read crop health metrics",
          text: "Total crop health (0–1), total performance (%), field cover (%), LAI (leaf area index), and Kcb (basal crop coefficient). View Full Crop Analysis opens the time-series chart for each metric.",
        },
        {
          type: "step",
          title: "Cross-check NDVI and operational controls",
          text: "The satellite widgets show overall NDVI, farming percentage, poor areas, and irrigation drought areas with week-over-week deltas. The Irrigation and Fertilization panels below let you act on what you see.",
        },
        {
          type: "cta",
          title: "Open the crop wiki",
          text: "Pick a crop to read FAO-56 Kc, NDVI guidance, and per-stage fertilization targets.",
          button: "Open crops",
        },
      ],
    },
    ar: {
      title: "افتح موسوعة المحاصيل لحقلك",
      eyebrow: "الخطوة 14 من 15",
      blurb:
        "لوحة بيانات المحصول لكل حقل: FAO-56 Kc وNDVI وLAI وتحليلات الأقمار وأهداف التسميد وحالة الري — في شاشة واحدة.",
      imageAlt:
        "مخطط تفاصيل الحقل في FarmTopia بشريط تبويبات علوي (الحقل 1، الحقل 2 محدد، الحقل 3، رؤية المزيد)، مسار تنقل (الرئيسية > الحقول > الحقل 2)، بطاقة 'الحقل 1 — 50 أكر، الفرافرة، غرب مصر، ملكية حكومية'، ثم أقسام للمعلومات الأساسية للمحصول، مقاييس صحة المحصول مع رابط 'عرض التحليل الكامل للمحصول'، الطقس، أدوات تحليل الأقمار، وأدوات تشغيلية للري والتسميد بشريط 'إجمالي صحة الري' عند 20%. صور الأقمار على اليمين برسم وردي لحدود الحقل.",
      body: [
        {
          type: "paragraph",
          text: "لوحة بيانات المحصول لكل حقل تعرض المعلومات الأساسية (تاريخ الزراعة، الحصاد المتوقع، الإنفاق المخطط)، مقاييس الصحة الحية (NDVI، LAI، Kcb، الأداء الكلي)، الطقس، وأدوات تشغيل الري والتسميد — كلها في شاشة واحدة.",
        },
        {
          type: "paragraph",
          text: "هذه الصفحة التي يفتحها المهندسون الزراعيون أكثر من غيرها. كل ما تعرفه FarmTopia عن حقل واحد، منظَّم كمدخل موسوعة: الهوية في الأعلى، المؤشرات الحيوية في الوسط، الأدوات في الأسفل. اقرنها بخريطة الأقمار على اليمين، وتستطيع إجراء زيارة حقل دون مغادرة مكتبك.",
        },
        {
          type: "step",
          title: "انتقل بين حقولك",
          text: "شريط التبويبات العلوي يعرض كل حقل في هذه المزرعة. 'رؤية المزيد' يفتح المنتقي الكامل إذا كان لديك أكثر من أربعة. مسار التنقل (الرئيسية > الحقول > الحقل 2) يعمل أيضاً كرجوع سريع.",
        },
        {
          type: "step",
          title: "اقرأ بطاقة الهوية",
          text: "اسم الحقل ومساحته بالأكر وموقعه ووسم الملكية (حكومية مقابل خاصة). أيقونة التعديل في الزاوية تتيح تصحيح المساحة أو إعادة تسمية الحقل دون العودة لمسار 'إضافة حقل'.",
        },
        {
          type: "step",
          title: "تفقّد المعلومات الأساسية للمحصول",
          text: "تاريخ الزراعة، الحصاد المتوقع، الإنفاق المخطط، الإنتاجية المتوقعة، إجمالي الإنفاق حتى الآن. التواريخ تغذّي منحنى FAO-56 Kc حتى تعرف صفحة الري في أي مرحلة نمو أنت.",
        },
        {
          type: "step",
          title: "اقرأ مقاييس صحة المحصول",
          text: "إجمالي صحة المحصول (0-1)، الأداء الكلي (%)، تغطية الحقل (%)، LAI (مؤشر مساحة الورق)، وKcb (المعامل المحصولي القاعدي). 'عرض التحليل الكامل للمحصول' يفتح المخطط الزمني لكل مقياس.",
        },
        {
          type: "step",
          title: "قارن NDVI مع الأدوات التشغيلية",
          text: "أدوات الأقمار تعرض NDVI الإجمالي ونسبة الزراعة والمناطق الضعيفة ومناطق جفاف الري مع فروق أسبوعية. لوحتا الري والتسميد بالأسفل تتيحان التصرّف بناءً على ما ترى.",
        },
        {
          type: "cta",
          title: "افتح موسوعة المحاصيل",
          text: "اختر محصولاً لقراءة FAO-56 Kc وإرشادات NDVI وأهداف التسميد لكل مرحلة.",
          button: "افتح المحاصيل",
        },
      ],
    },
  },
  {
    slug: "connect-sensor",
    image: "/tutorial/connect-sensor.webp",
    en: {
      title: "Connect sensors and control devices",
      eyebrow: "Step 15 of 15",
      blurb:
        "Bring soil probes, weather stations, and valve controllers into FarmTopia over LoRaWAN or a vendor cloud — then read live data and open valves from the app.",
      imageAlt:
        "FarmTopia connect-hardware wireframe showing a vendor cloud token form with a provider dropdown, a poll-frequency selector with a live monthly cost estimate, and a supported-hardware coverage matrix for LoRaWAN devices and cloud providers.",
      body: [
        {
          type: "paragraph",
          text: "Bring your own IoT hardware into FarmTopia — soil probes, weather stations, valve controllers, and more. Connect over a LoRaWAN webhook or a vendor cloud token, read live readings on the field, and open or close valves right from the app.",
        },
        {
          type: "paragraph",
          text: "FarmTopia is hardware-agnostic and you don't host a network server — readings flow from your gateway or vendor cloud into the same telemetry the platform already uses, so a field metric simply flips from 'Model' to 'Sensor' once a device reports. Control works the other way: a valve command is queued, dispatched, and confirmed when the valve reports its new state.",
        },
        {
          type: "step",
          title: "Choose how to connect",
          text: "Open Devices → Connect hardware. Pick a path: a LoRaWAN webhook (your network server — TTN, ChirpStack, Milesight — posts uplinks to FarmTopia) or a vendor cloud token (FarmTopia polls the vendor API on a schedule). You never run a server.",
        },
        {
          type: "step",
          title: "Set the poll frequency and check the cost",
          text: "For cloud-token integrations, choose how often FarmTopia polls — every 5 minutes up to daily. The page shows the live monthly cost of that cadence: reading volume is fixed by your device, so polling faster mainly buys lower latency, not more data.",
        },
        {
          type: "step",
          title: "Bind devices to a field",
          text: "Attach each device to a farm and field (a weather station can be farm-wide) so its readings land in the right place. The Devices page then shows every device with live status — online/offline, battery, and signal.",
        },
        {
          type: "step",
          title: "Read live data and set alert rules",
          text: "On the field page the soil and weather cards light up with live values, each tagged Sensor, Model, or Forecast so you know the source. From Devices, set per-device alert rules (e.g. soil moisture below 20% → warning); breaches appear in the alert bell.",
        },
        {
          type: "step",
          title: "Control valves from the app",
          text: "On the irrigation field page, valve controllers show Open/Close controls. A command is queued and confirmed when the valve reports back. Control is permission-gated — owners and managers can actuate; viewers see a read-only view.",
        },
        {
          type: "cta",
          title: "Connect your first device",
          text: "Bring a soil probe, weather station, or valve controller into FarmTopia.",
          button: "Open the connect page",
        },
      ],
    },
    ar: {
      title: "وصّل المستشعرات وتحكّم في الأجهزة",
      eyebrow: "الخطوة 15 من 15",
      blurb:
        "أدخِل مجسّات التربة ومحطات الطقس ومتحكمات الصمامات إلى FarmTopia عبر LoRaWAN أو سحابة المورّد — ثم اقرأ البيانات الحية وافتح الصمامات من التطبيق.",
      imageAlt:
        "مخطط صفحة توصيل العتاد في FarmTopia يعرض نموذج رمز سحابة المورّد مع قائمة منسدلة للمزوّد، ومحدِّد تردد الاستطلاع مع تقدير شهري حيّ للتكلفة، ومصفوفة دعم للأجهزة عبر LoRaWAN ومزوّدي السحابة.",
      body: [
        {
          type: "paragraph",
          text: "أدخِل أجهزة إنترنت الأشياء الخاصة بك إلى FarmTopia — مجسّات التربة ومحطات الطقس ومتحكمات الصمامات وغيرها. وصّل عبر ويب-هوك LoRaWAN أو رمز سحابة المورّد، اقرأ القراءات الحية في الحقل، وافتح أو أغلق الصمامات من التطبيق مباشرة.",
        },
        {
          type: "paragraph",
          text: "FarmTopia مستقلة عن نوع العتاد ولا تستضيف خادم شبكة — تتدفّق القراءات من بوّابتك أو سحابة المورّد إلى نفس بيانات القياس التي تستخدمها المنصة، فتتحوّل قيمة الحقل من 'نموذج' إلى 'مستشعر' بمجرّد أن يبدأ الجهاز بالإرسال. والتحكّم يسير بالاتجاه المعاكس: يُوضع أمر الصمام في الطابور ثم يُرسَل، ويُؤكَّد عندما يبلّغ الصمام عن حالته الجديدة.",
        },
        {
          type: "step",
          title: "اختر طريقة التوصيل",
          text: "افتح الأجهزة ← توصيل العتاد. اختر مساراً: ويب-هوك LoRaWAN (يُرسل خادم شبكتك — TTN أو ChirpStack أو Milesight — القراءات إلى FarmTopia)، أو رمز سحابة المورّد (تستطلع FarmTopia واجهة المورّد دورياً). أنت لا تشغّل أي خادم.",
        },
        {
          type: "step",
          title: "اضبط تردد الاستطلاع وتحقّق من التكلفة",
          text: "في تكاملات الرمز السحابي اختر معدّل الاستطلاع — من كل 5 دقائق حتى يومياً. تعرض الصفحة التكلفة الشهرية الحية لهذا المعدّل: حجم القراءات يحدّده جهازك، فالاستطلاع الأسرع يشتري زمن استجابة أقل لا بيانات أكثر.",
        },
        {
          type: "step",
          title: "اربط الأجهزة بحقل",
          text: "اربط كل جهاز بمزرعة وحقل (يمكن أن تكون محطة الطقس على مستوى المزرعة) حتى تصل قراءاته إلى المكان الصحيح. تعرض صفحة الأجهزة بعدها كل جهاز بحالته الحية — متصل/غير متصل والبطارية والإشارة.",
        },
        {
          type: "step",
          title: "اقرأ البيانات الحية واضبط قواعد التنبيه",
          text: "في صفحة الحقل تضيء بطاقتا التربة والطقس بالقيم الحية، وكل قيمة موسومة بـ مستشعر أو نموذج أو توقّع لتعرف مصدرها. ومن الأجهزة اضبط قواعد تنبيه لكل جهاز (مثلاً رطوبة التربة أقل من 20% ← تحذير)؛ وتظهر التجاوزات في جرس التنبيهات.",
        },
        {
          type: "step",
          title: "تحكّم في الصمامات من التطبيق",
          text: "في صفحة ري الحقل تعرض متحكمات الصمامات أزرار فتح/إغلاق. يُوضع الأمر في الطابور ويُؤكَّد عندما يبلّغ الصمام عن حالته. والتحكّم محكوم بالصلاحيات — المالكون والمديرون يمكنهم التشغيل، أما المشاهدون فيرون عرضاً للقراءة فقط.",
        },
        {
          type: "cta",
          title: "وصّل جهازك الأول",
          text: "أدخِل مجس تربة أو محطة طقس أو متحكم صمام إلى FarmTopia.",
          button: "افتح صفحة التوصيل",
        },
      ],
    },
  },
];

/** Look up a section by slug. Returns undefined for unknown slugs. */
export function getTutorialSection(slug) {
  return TUTORIAL_CONTENT.find((s) => s.slug === slug);
}
